import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { useCustom, useTranslate } from "@refinedev/core";

import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { heeftRondleiding } from "../rondleiding/rondleidingen";
import { BewaardeAnalyses } from "./BewaardeAnalyses";
import { voerAnalyseUit, exporteerExcel } from "../api/insights";
import { Samenvatting } from "./Samenvatting";
import {
  HeartIcon,
  PawIcon,
  UserIcon,
  InvoiceIcon,
  HomeIcon,
  ClockIcon,
  BoltIcon,
  LightbulbIcon,
  FilterIcon,
  AlertTriangleIcon,
  SearchIcon,
  SparklesIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  XIcon,
  TrashIcon,
  CheckIcon,
} from "@fawkes/ui";

import {
  MIN_CHARS,
  RESULTS_LIMIT,
  groupTypeIcon,
  splitAnimalTitle,
  facetLabelKey,
  animalStatusBadgeKey,
  ANIMAL_STATUS_VARIANT,
  type SearchHitDto,
  type SearchHitType,
  type SearchResponseDto,
  type SearchFacetCountsDto,
} from "../components/searchShared";
import {
  fromSearch,
  toQueryString,
  emptyFilters,
  hasAnyDomainFilter,
  toggleCode,
  ANIMAL_STATUSES,
  SPECIES,
  RELATION_TYPES,
  RELATION_ROLES,
  INVOICE_STATUSES,
  SORT_ORDERS,
  PERIODS,
  type SearchFilters,
  type Period,
  type ResultType,
  type SortOrder,
  type AnimalStatus,
  type Species,
  type RelationType,
  type InvoiceStatus,
} from "../searchFilters";

// Search-migration E4 (FR17–FR23) — the full `/zoekresultaten` results page, at feature parity with the
// Jmix `SearchResultsView`: a 3-column layout (left facets / middle results / right sidebar), type tabs
// with FILTERED counts, a left facet panel with UNFILTERED counts, removable active-filter chips,
// "Bekijk alle <type>" show-more links, the recente/snelle-acties/tip sidebar, the empty/error states,
// real sort + client-side paging, and — when `ai=1` — the per-mode AI interpretation panel.
//
// Decision SS: classification + the unfiltered facet counts are server-side (on each E1 hit + the
// response `facets`); the page filters CLIENT-SIDE (the `GET /api/search` endpoint takes only q+limit —
// facet filtering is not a server param), exactly as Jmix did, so toggling a facet never refetches.
// Decision URL: the full filter state mirrors to the URL via `searchFilters.ts` and seeds the page on
// entry. Decision PAGING: client-side slicing within the per-type cap (the Jmix paginator was a mock).
//
// Declared divergences (parity = match; divergences flagged, per the epic guardrails):
//  • Mocks dropped (Decision MOCKS): the top-row Type/Periode pill dropdowns, the dead filter-row
//    "Sorteren" button, and the `period` URL param — no inert filters shipped. The real sort is the
//    left-panel select; the real paging is client-side.
//  • FOSTER status row kept (always count 0) — the Jmix source renders it in the status section
//    (SearchResultsView ~L1285); the classifier never produces `foster`. Source wins over the doc prose.
//  • Invoice cards apply the invoice-status filter (Jmix `buildInvoiceSection` rendered RAW invoiceHits —
//    the filter drove only the tab count/empty-state, not the cards; a Jmix inconsistency). Aligned with
//    the animal/relation sections + the tab count.
//  • Show-more reconciles the dead Jmix `tab=` param (never parsed by SearchFilters) to the canonical
//    `type=` so it actually switches to the type tab. On a type tab, paging replaces show-more.
//  • Real sort + client paging (FR22, declared enhancement): the left-panel select drives the sort —
//    name_az by title (Jmix parity) and newest/oldest by admissionDate. Jmix's newest/oldest were
//    no-ops (only name_az sorted; the others fell through to backend order); paging slices within the
//    per-type cap on a single-type tab (the Jmix paginator was a non-functional mock).
//  • Per-mode AI panel (FR21, flagged enhancement): the Jmix `ai=1` panel does not branch on mode; the
//    per-mode copy is lifted from the unrendered `runAiPreview` / `AISearchPanel`. AI mode renders the
//    panel + result cards (with per-item detail labels) + the sidebar; the deterministic facet
//    panel/tabs are literal-search only (`/execute` ships detail labels, not the facet classification).
//  • Recent searches use the real localStorage contract (`fawkes.search.recent`, the E3 GlobalSearch
//    store), not the Jmix hard-coded mock list. On `/execute` failure/disabled (403/404) the page falls
//    back to the literal results (+ an "unavailable" note), matching Jmix's fall-through to normal search.

const RECENT_KEY = "fawkes.search.recent";
const RECENT_MAX = 5;

type Tab = "alles" | "dieren" | "relaties" | "facturen" | "verblijven";
const TAB_ORDER: Tab[] = ["alles", "dieren", "relaties", "facturen", "verblijven"];
const TAB_TO_TYPE: Record<Exclude<Tab, "alles">, ResultType> = {
  dieren: "animal",
  relaties: "relation",
  facturen: "invoice",
  verblijven: "stay",
};
const TYPE_TO_TAB: Record<ResultType, Exclude<Tab, "alles">> = {
  animal: "dieren",
  relation: "relaties",
  invoice: "facturen",
  stay: "verblijven",
};
const ALL_TYPES: ResultType[] = ["animal", "relation", "invoice", "stay"];

/** Tab → its display-label i18n key (reuses the E3 `search.*` group labels). */
const TAB_LABEL_KEY: Record<Tab, string> = {
  alles: "search.tab.all",
  dieren: "search.group.animals",
  relaties: "search.group.relations",
  facturen: "search.group.invoices",
  verblijven: "search.group.shelters",
};
/** Result-type → its display-label i18n key (the "Niet getoond: X" + type-result-checkbox labels). */
const TYPE_LABEL_KEY: Record<ResultType, string> = {
  animal: "search.group.animals",
  relation: "search.group.relations",
  invoice: "search.group.invoices",
  stay: "search.group.shelters",
};

// ===== AI execute (E2) response shape =====
interface ExecuteDetailDto {
  label: string;
  value: string;
}
interface ExecuteItemDto {
  type: string;
  id: string;
  title: string;
  subtitle: string | null;
  initials: string;
  targetType: string | null;
  targetId: string | null;
  details: ExecuteDetailDto[];
}
/** Een taak uit de wegwijzer: het scherm en de stappen. De stappen zijn geschreven, niet
 *  gegenereerd — zie HowToCatalogus in de api. */
interface HowToDto {
  id: string;
  titel: string;
  route: string | null;
  stappen: string[];
}

interface ExecuteResponseDto {
  mode: string;
  confidence: number;
  explanation: string | null;
  primaryEntity: string | null;
  suggestions: string[];
  usedDataAreas: string[];
  unsupportedParts: string[];
  question: string | null;
  rejectionReason: string | null;
  /** Alleen bij mode "how_to": waar de handeling gebeurt en hoe. */
  howTo: HowToDto | null;
  /**
   * Kan de vraag twee kanten op, dan staan hier de lezingen en is howTo leeg. Het scherm hoort
   * dan te vragen wat er bedoeld wordt in plaats van er zelf een te kiezen.
   */
  howToKeuze?: HowToDto[] | null;
  total: number;
  tookMs: number;
  error: string | null;
  items: ExecuteItemDto[];
  /**
   * Alleen bij een samenvatting: getallen per groep in plaats van regels. Leeg bij een lijstvraag.
   * Aan het aantal labels leest het scherm af welke weergave past: geen labels is één getal, één
   * label een reeks, twee labels een gestapelde reeks.
   */
  groepen?: { labels: string[]; waarde: number | null; zonderWaarde?: number }[] | null;
  /** Wat er gerekend is: AANTAL of GEMIDDELDE. Leeg bij een lijstvraag. */
  maat?: string | null;
  /** Waarop er is uitgesplitst, in dezelfde volgorde als de labels in groepen. */
  groeperingen?: string[] | null;
}

function readRecent(): string[] {
  try {
    const raw = globalThis.localStorage?.getItem(RECENT_KEY);
    const list: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    return list.filter((x): x is string => typeof x === "string").slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

/** Tab implied by the current result-type set (Jmix beforeEnter "Optie A"): one type → that tab, else Alles. */
function deriveTab(resultTypes: ResultType[]): Tab {
  return resultTypes.length === 1 ? TYPE_TO_TAB[resultTypes[0]] : "alles";
}

/**
 * Calendar-days between an ISO admission date and today (Jmix ChronoUnit.DAYS.between over LocalDate).
 * Both sides are pinned to UTC midnight so the count is timezone/DST-independent (no off-by-one).
 */
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const then = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((today - then) / 86_400_000);
}

export interface SearchResultsProps {
  query: string;
  ai: boolean;
  /** Open an entity detail via the shell's state nav (structured {type,id} target — Decision NAV). */
  onOpenHit: (targetType: SearchHitType, targetId: string, fallbackQuery: string) => void;
  /**
   * Mirror a new filter/search state to the URL + shell state: `search` is the complete query string
   * (incl. `q` and `ai=1` when present), `query`/`ai` the parsed values the shell tracks.
   */
  onSearchNavigate: (search: string, query: string, ai: boolean) => void;
  /** The four "Snelle acties" — map to the shell's state nav / legacy hand-off (Jmix quick-action routes). */
  onQuickAction: (action: ResultType) => void;
  /** Brengt de gebruiker naar het scherm van een wegwijzer-taak. */
  onOpenRoute: (route: string) => void;
  /**
   * Start de rondleiding bij deze taak. De rondleiding zelf hangt in de schil en niet hier: zodra
   * hij je naar een ander scherm brengt verdwijnt dit component, en een rondleiding die halverwege
   * oplost is erger dan geen rondleiding.
   */
  onStartRondleiding: (taakId: string) => void;
}

export function SearchResults({ query, ai, onOpenHit, onSearchNavigate, onQuickAction, onOpenRoute, onStartRondleiding }: SearchResultsProps) {

  const t = useTranslate();

  // Filter state lives in the URL (Decision URL); seed every filter from it, but take `q` from the prop —
  // the shell's authoritative query (App seeds it from the same URL on entry, and an empty prop must stay
  // empty, e.g. after a removed query chip, rather than falling back to a stale URL `q`).
  const seed = useCallback((): SearchFilters => {
    return { ...fromSearch(globalThis.location?.search ?? ""), query: (query ?? "").trim() };
  }, [query]);
  const [filters, setFilters] = useState<SearchFilters>(seed);
  const [recent, setRecent] = useState<string[]>(() => readRecent());

  // Re-seed when the shell pushes a new query/ai (header handoff) — and on browser back/forward, so the
  // deep-linkable filter state survives history navigation (FR23).
  useEffect(() => {
    setFilters(seed());
  }, [query, ai, seed]);
  useEffect(() => {
    const onPop = () => setFilters(fromSearch(globalThis.location?.search ?? ""));
    globalThis.addEventListener?.("popstate", onPop);
    return () => globalThis.removeEventListener?.("popstate", onPop);
  }, []);

  const q = filters.query.trim();
  const activeTab = deriveTab(filters.resultTypes);

  // Tablist a11y (WAI-ARIA tabs pattern, manual activation): a single tab stop with roving tabindex +
  // arrow/Home/End focus movement; Enter/Space (native button) activates. `focusedTab` is the roving
  // anchor and resets to follow the active tab after any (re)selection.
  const tabsBaseId = useId();
  const [focusedTab, setFocusedTab] = useState<Tab | null>(null);
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});
  const rovingTab = focusedTab ?? activeTab;
  useEffect(() => setFocusedTab(null), [activeTab]);

  // ===== AI smart-search (ai=1): one /execute call interprets + auto-runs the plan (CONF1) =====
  const aiEnabled = ai && q.length >= MIN_CHARS;
  /**
   * De aangeklikte staaf, of null. Dit is de drill-down uit het ontwerp: dezelfde vraag, maar de
   * records onder díé groep. De omzetting van labels naar voorwaarden gebeurt op de server — daar
   * staat het schema, en alleen daar is bekend dat "2026-01" een maand is en dus een bereik.
   */
  const [uitgeklapt, setUitgeklapt] = useState<string[] | null>(null);
  const [lijstExportBezig, setLijstExportBezig] = useState(false);
  const [lijstExportFout, setLijstExportFout] = useState<string | null>(null);
  useEffect(() => setUitgeklapt(null), [q]);
  /**
   * De uitkomst van een geopende bewaarde analyse. Dit komt niet uit de zoekbalk maar uit de
   * opgeslagen definitie, en overschrijft daarom wat er van /search/execute kwam — anders zou je
   * twee antwoorden tegelijk zien.
   */
  const [analyseResultaat, setAnalyseResultaat] = useState<ExecuteResponseDto | null>(null);
  useEffect(() => setAnalyseResultaat(null), [q]);
  const {
    data: aiData,
    isFetching: aiFetching,
    isError: aiError,
  } = useCustom<ExecuteResponseDto>({
    // De url draagt de gekozen staaf mee zodat het antwoord niet uit de cache van de vraag zónder
    // staaf komt; de vraag zelf blijft dezelfde.
    url: `search/execute?q=${encodeURIComponent(q)}${
      uitgeklapt ? `&groep=${encodeURIComponent(uitgeklapt.join("|"))}` : ""}`,
    method: "post",
    config: { payload: { query: q, groep: uitgeklapt ?? undefined } },
    queryOptions: { enabled: aiEnabled, retry: false, keepPreviousData: true },
  });
  // Een geopende bewaarde analyse wint van de zoekvraag: die is bewust aangeklikt.
  const aiResult = analyseResultaat ?? (aiEnabled ? aiData?.data : undefined);
  /**
   * Welke lezing de gebruiker koos toen de vraag twee kanten op kon. Dit hoort in het scherm en
   * niet in het antwoord: kiezen moet je kunnen overdoen zonder opnieuw te zoeken. Bij een nieuwe
   * vraag vervalt de keuze vanzelf, want die hangt aan de tekst van de vraag.
   */
  const [gekozen, setGekozen] = useState<string | null>(null);
  useEffect(() => setGekozen(null), [q]);
  const keuze = aiResult?.howToKeuze ?? [];
  const getoondeHowTo =
    aiResult?.howTo ?? (gekozen ? keuze.find((k) => k.id === gekozen) ?? null : null);
  // The AI UI is shown while a smart search is live AND reachable; a transport/403/404 (disabled or
  // missing ROLE_SEARCH_AI) falls back to the literal results (Jmix fell through to normal search).
  const aiActive = aiEnabled && !aiError;

  // ===== Deterministic literal search (E1): one fetch keyed on q; facets are client-side =====
  const literalEnabled = q.length >= MIN_CHARS && (!ai || aiError);
  const {
    data: litData,
    isFetching: litFetching,
    isError: litError,
  } = useCustom<SearchResponseDto>({
    url: `search?q=${encodeURIComponent(q)}&limitPerType=${RESULTS_LIMIT}`,
    method: "get",
    queryOptions: { enabled: literalEnabled, keepPreviousData: true },
  });
  const results = literalEnabled ? litData?.data : undefined;
  const facets: SearchFacetCountsDto = (results?.facets as SearchFacetCountsDto) ?? {};

  const rawByType = (type: ResultType): SearchHitDto[] => {
    const key = type === "animal" ? "animals" : type === "relation" ? "relations" : type === "invoice" ? "invoices" : "shelters";
    return (results?.groups as Record<string, SearchHitDto[] | undefined>)?.[key] ?? [];
  };

  // ===== Navigation helpers (every mutation mirrors to the URL + resets page to 1, like Jmix) =====
  const applyFilters = useCallback(
    (next: SearchFilters) => {
      const f = { ...next, page: 1 };
      setFilters(f);
      onSearchNavigate(toQueryString(f), f.query, false);
    },
    [onSearchNavigate],
  );
  const goToPage = useCallback(
    (page: number) => {
      const f = { ...filters, page };
      setFilters(f);
      onSearchNavigate(toQueryString(f), f.query, false);
    },
    [filters, onSearchNavigate],
  );
  const navigatePlainQuery = useCallback(
    (term: string) => {
      const f: SearchFilters = { ...emptyFilters(), query: term.trim() };
      onSearchNavigate(toQueryString(f), f.query, false);
    },
    [onSearchNavigate],
  );
  const navigateAiQuery = useCallback(
    (term: string) => {
      const f: SearchFilters = { ...emptyFilters(), query: term.trim() };
      const qs = toQueryString(f);
      onSearchNavigate(qs ? `ai=1&${qs}` : "ai=1", f.query, true);
    },
    [onSearchNavigate],
  );

  const clearAllFilters = () =>
    // Wis filters/alles: reset every filter but keep the query + the current sort/pageSize (Jmix
    // clearAllFilters does not touch sortOption/pageSize).
    applyFilters({ ...emptyFilters(), query: filters.query, sort: filters.sort, pageSize: filters.pageSize });

  const rememberCleared = () => {
    try {
      globalThis.localStorage?.removeItem(RECENT_KEY);
      globalThis.dispatchEvent?.(new Event("fawkes-recent-search-updated"));
    } catch {
      /* ignore */
    }
    setRecent([]);
  };

  // ===== Client-side filtering (Jmix filtered*Hits) + sort =====
  const filteredAnimals = (): SearchHitDto[] => {
    const minDays = filters.minDaysPresent;
    return rawByType("animal").filter((h) => binnenPeriode(h, filters.period)).filter((h) => {
      // An "unknown"/null code is never in a specific filter set, so the hit is excluded (correct).
      if (filters.animalStatuses.length && !filters.animalStatuses.includes((h.animalStatus ?? "unknown") as AnimalStatus))
        return false;
      if (filters.species.length && !filters.species.includes((h.species ?? "unknown") as Species)) return false;
      if (minDays > 0) {
        const d = daysSince(h.admissionDate);
        if (d == null || d < minDays) return false;
      }
      return true;
    });
  };
  const filteredRelations = (): SearchHitDto[] =>
    rawByType("relation").filter((h) => {
      if (filters.relationTypes.length && !filters.relationTypes.includes((h.relationType ?? "unknown") as RelationType))
        return false;
      if (filters.relationRoles.length) {
        const roles = h.relationRoles ?? [];
        if (!filters.relationRoles.some((r) => roles.includes(r))) return false;
      }
      return true;
    });
  const filteredInvoices = (): SearchHitDto[] =>
    rawByType("invoice").filter(
      (h) => !filters.invoiceStatuses.length || filters.invoiceStatuses.includes((h.invoiceStatus ?? "unknown") as InvoiceStatus),
    );
  const filteredStays = (): SearchHitDto[] => rawByType("stay");

  const filteredByType = (type: ResultType): SearchHitDto[] =>
    type === "animal"
      ? filteredAnimals()
      : type === "relation"
        ? filteredRelations()
        : type === "invoice"
          ? filteredInvoices()
          : filteredStays();

  const sortHits = (hits: SearchHitDto[]): SearchHitDto[] => {
    const copy = [...hits];
    switch (filters.sort) {
      case "name_az":
        return copy.sort((a, b) =>
          (a.title ?? "").toLowerCase().localeCompare((b.title ?? "").toLowerCase(), "nl"),
        );
      case "newest":
        return copy.sort((a, b) => compareAdmission(a, b, -1));
      case "oldest":
        return copy.sort((a, b) => compareAdmission(a, b, 1));
      default:
        return copy; // relevance = backend order
    }
  };

  const sortedFilteredByType = (type: ResultType): SearchHitDto[] => sortHits(filteredByType(type));

  // ===== Counts: tabs FILTERED (Decision SS), left facets UNFILTERED (server `facets`) =====
  const filteredCount: Record<ResultType, number> = {
    animal: filteredAnimals().length,
    relation: filteredRelations().length,
    invoice: filteredInvoices().length,
    stay: filteredStays().length,
  };

  // Which type's results are shown: a specific tab scopes to one type; on Alles the type-result
  // checkboxes (resultTypes set) decide. resultTypes empty = all on.
  const typeShown = (type: ResultType): boolean =>
    activeTab === "alles"
      ? filters.resultTypes.length === 0 || filters.resultTypes.includes(type)
      : TAB_TO_TYPE[activeTab] === type;

  const singleType: ResultType | null = activeTab === "alles" ? null : TAB_TO_TYPE[activeTab];

  // De foto bij elke diertreffer, zoals op acc: je herkent het dier voordat je de naam leest. De
  // bytes zitten achter de bearer, dus een <img src> haalt ze niet zelf op — deze hook doet dat met
  // de token erbij, vier tegelijk zodat de lijst de rest van het scherm niet ophoudt. Geen foto
  // (of nog aan het laden) valt terug op het pootje, precies zoals het hiervoor stond.
  const animalPhotoIds = useMemo(
    () => [
      ...rawByType("animal").map((hit) => String(hit.targetId ?? hit.id)),
      // Ook de treffers van slim zoeken: die komen uit /execute en niet uit de letterlijke zoek,
      // dus zonder deze regel bleef juist het scherm waar de operator naar keek zonder foto's.
      ...(aiResult?.items ?? [])
        .filter((item) => item.type === "animal")
        .map((item) => String(item.targetId ?? item.id)),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      rawByType("animal").map((hit) => hit.id).join(","),
      (aiResult?.items ?? []).map((item) => item.id).join(","),
    ],
  );
  const animalPhotoUrls = useAnimalPhotoUrls(animalPhotoIds);

  // ===== Render =====
  return (
    <div className={`fawkes-search-results${aiActive ? " fawkes-search-results--ai" : ""}`}>
      {renderHeader()}
      {aiActive ? (
        <div className="fawkes-search-results__ai-layout">
          <div className="fawkes-search-results__main">{renderAi()}</div>
          <aside className="fawkes-search-results__right">{renderSidebar()}</aside>
        </div>
      ) : (
        <>
          {ai && aiError ? (
            <p className="fawkes-search-results__ai-note">{t("zoekresultaten.ai.unavailable")}</p>
          ) : null}
          {renderTabs()}
          {renderFilterRow()}
          {renderChips()}
          <div className="fawkes-search-results__content">
            <aside className="fawkes-search-results__left">{renderLeftFilters()}</aside>
            <div
              className="fawkes-search-results__middle"
              role="tabpanel"
              id={`${tabsBaseId}-panel`}
              aria-labelledby={`${tabsBaseId}-${activeTab}`}
              tabIndex={0}
            >
              {renderMiddle()}
            </div>
            <aside className="fawkes-search-results__right">{renderSidebar()}</aside>
          </div>
          {renderPagination()}
        </>
      )}
    </div>
  );

  // ---- header ----
  function renderHeader(): ReactNode {
    let subtitle: string;
    if (q.length < MIN_CHARS) {
      subtitle = t("search.results.prompt");
    } else if (!aiActive && litError) {
      subtitle = t("zoekresultaten.subtitle.error");
    } else if (aiActive) {
      subtitle = aiFetching ? t("common.loading") : "";
    } else if (litFetching) {
      subtitle = t("common.loading");
    } else {
      const total = results?.total ?? 0;
      subtitle =
        total === 0
          ? ""
          : t(total === 1 ? "zoekresultaten.subtitle.one" : "zoekresultaten.subtitle.many", { args: [total, q] });
    }
    // Persistent polite live region: a screen reader announces the updated count after a filter toggle
    // (kept mounted even when empty so the announcement fires on change).
    // De grote kop erboven, zoals op acc. Hij stond er niet, en dan begint de pagina met een grijze
      // regel tekst — je ziet pas aan de inhoud waar je bent beland.
    return (
      <>
        <h1 className="fawkes-search-results__title">{t("search.results.title")}</h1>
        <p className="fawkes-search-results__subtitle" role="status" aria-live="polite">
          {subtitle}
        </p>
      </>
    );
  }

  // ---- tabs (FILTERED counts) ----
  function onTabsKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(e.key)) return;
    e.preventDefault();
    const i = TAB_ORDER.indexOf(rovingTab);
    const n = TAB_ORDER.length;
    const ni =
      e.key === "ArrowRight" ? (i + 1) % n : e.key === "ArrowLeft" ? (i - 1 + n) % n : e.key === "Home" ? 0 : n - 1;
    const next = TAB_ORDER[ni];
    setFocusedTab(next);
    tabRefs.current[next]?.focus();
  }

  function renderTabs(): ReactNode {
    return (
      <div
        className="fawkes-search-results__tabs"
        role="tablist"
        aria-label={t("zoekresultaten.tabs.label")}
        onKeyDown={onTabsKeyDown}
      >
        {TAB_ORDER.map((tab) => {
          const count = tab === "alles" ? null : filteredCount[TAB_TO_TYPE[tab]];
          const selected = tab === activeTab;
          return (
            <button
              key={tab}
              ref={(el) => {
                tabRefs.current[tab] = el;
              }}
              type="button"
              role="tab"
              id={`${tabsBaseId}-${tab}`}
              aria-selected={selected}
              aria-controls={`${tabsBaseId}-panel`}
              tabIndex={tab === rovingTab ? 0 : -1}
              className={`fawkes-search-results__tab${selected ? " fawkes-search-results__tab--active" : ""}`}
              onClick={() =>
                applyFilters({ ...filters, resultTypes: tab === "alles" ? [] : [TAB_TO_TYPE[tab]] })
              }
            >
              <span>{t(TAB_LABEL_KEY[tab])}</span>
              {count != null ? <span className="fawkes-search-results__tab-count">({count})</span> : null}
            </button>
          );
        })}
      </div>
    );
  }

  // ---- filter row (mocks dropped: only the funnel + "Wis filters") ----
  /**
   * De filterbalk: trechter, "Wis filters", en rechts het sorteren.
   *
   * <p>Het sorteren stond in het linkerpaneel, onder alle facetten — daar keek niemand. Op acc zit
   * het rechtsboven en dat is waar de operator het zocht. Wat er in Jmix staat is overigens een
   * dood element: een Div met een pijltje en geen enkele klik-afhandeling. Dit is dezelfde plek,
   * maar dan de werkende keuzelijst die er al was.
   *
   * <p>Die twee andere pillen uit Jmix ("Type: Alles", "Periode: Alles") komen er niet bij: die zijn
   * daar óók dood, en een filter dat niets filtert is erger dan een filter dat er niet is.
   */
  function renderFilterRow(): ReactNode {
    return (
      <div className="fawkes-search-results__filter-row">
        {/* De trechter: op acc een rond knopje met een randje, puur decoratief. */}
        <span className="fawkes-search-results__filter-icon" aria-hidden="true">
          <FilterIcon width={15} height={15} />
        </span>
        {/* Type en Periode als pillen. Op acc zijn het lege Divs met een pijltje; hier sturen ze
            echt iets aan — Type dezelfde staat als de tabbladen erboven, Periode de opnamedatum.
            De tekst staat volledig in de optie ("Type: Alles"), zoals daar, en het eigen pijltje
            van de browser is weggehaald zodat de chevron van het ontwerp overblijft. */}
        <span className="fawkes-search-results__pill">
          <span className="fawkes-search-results__pill-text">
            {`${t("zoekresultaten.filter.type")} ${t(TAB_LABEL_KEY[activeTab])}`}
          </span>
          <ChevronDownIcon className="fawkes-search-results__pill-chevron" width={12} height={12} />
          <select
            className="fawkes-search-results__pill-select"
            aria-label={t("zoekresultaten.filter.type")}
            value={activeTab}
            onChange={(e) => {
              const tab = e.target.value as Tab;
              applyFilters({
                ...filters,
                resultTypes: tab === "alles" ? [] : [TAB_TO_TYPE[tab]],
                page: 1,
              });
            }}
          >
            {TAB_ORDER.map((tab) => (
              <option key={tab} value={tab}>
                {`${t("zoekresultaten.filter.type")} ${t(TAB_LABEL_KEY[tab])}`}
              </option>
            ))}
          </select>
        </span>
        <span className="fawkes-search-results__pill">
          <span className="fawkes-search-results__pill-text">
            {`${t("zoekresultaten.filter.period")} ${t(`zoekresultaten.period.${filters.period}`)}`}
          </span>
          <ChevronDownIcon className="fawkes-search-results__pill-chevron" width={12} height={12} />
          <select
            className="fawkes-search-results__pill-select"
            aria-label={t("zoekresultaten.filter.period")}
            value={filters.period}
            onChange={(e) => applyFilters({ ...filters, period: e.target.value as Period, page: 1 })}
          >
            {PERIODS.map((period) => (
              <option key={period} value={period}>
                {`${t("zoekresultaten.filter.period")} ${t(`zoekresultaten.period.${period}`)}`}
              </option>
            ))}
          </select>
        </span>
        <button type="button" className="fawkes-search-results__link" onClick={clearAllFilters}>
          {t("zoekresultaten.action.clearFilters")}
        </button>
        <span className="fawkes-search-results__filter-spacer" />
        {/* "Sorteren op relevantie" als één zin, zoals op acc (daar: "Sorteren op " + het label in
            kleine letters). De keuzelijst eronder is echt; het knopje in Jmix is een dode Div. */}
        <span className="fawkes-search-results__sort">
          <SortArrowsIcon />
          <span className="fawkes-search-results__pill-text">
            {`${t("zoekresultaten.sort.prefix")} ${t(`zoekresultaten.sort.${filters.sort}`).toLowerCase()}`}
          </span>
          <ChevronDownIcon className="fawkes-search-results__pill-chevron" width={12} height={12} />
          <select
            className="fawkes-search-results__sort-select"
            aria-label={t("zoekresultaten.section.sort")}
            value={filters.sort}
            onChange={(e) => applyFilters({ ...filters, sort: e.target.value as SortOrder })}
          >
            {SORT_ORDERS.map((s) => (
              <option key={s} value={s}>
                {`${t("zoekresultaten.sort.prefix")} ${t(`zoekresultaten.sort.${s}`).toLowerCase()}`}
              </option>
            ))}
          </select>
        </span>
      </div>
    );
  }

  // ---- active-filter chips ----
  function renderChips(): ReactNode {
    const chips: ReactNode[] = [];
    const chip = (key: string, label: string, onRemove: () => void) =>
      chips.push(
        <span key={key} className="fawkes-search-results__chip">
          <span>{label}</span>
          <button
            type="button"
            className="fawkes-search-results__chip-x"
            aria-label={t("zoekresultaten.chip.remove", { args: [label] })}
            onClick={onRemove}
          >
            <XIcon width={13} height={13} />
          </button>
        </span>,
      );

    if (q) chip("q", t("zoekresultaten.chip.query", { args: [q] }), () => applyFilters({ ...filters, query: "" }));
    if (activeTab !== "alles")
      chip("tab", t("zoekresultaten.chip.tab", { args: [t(TAB_LABEL_KEY[activeTab])] }), () =>
        applyFilters({ ...filters, resultTypes: [] }),
      );

    // "Niet getoond: X" — only on Alles, for each type switched off via the type-result checkboxes.
    if (activeTab === "alles" && filters.resultTypes.length > 0) {
      for (const type of ALL_TYPES) {
        if (!filters.resultTypes.includes(type)) {
          chip(`hidden-${type}`, t("zoekresultaten.chip.hidden", { args: [t(TYPE_LABEL_KEY[type])] }), () => {
            const next = ALL_TYPES.filter((x) => filters.resultTypes.includes(x) || x === type);
            applyFilters({ ...filters, resultTypes: next.length === 4 ? [] : next });
          });
        }
      }
    }

    for (const code of filters.animalStatuses)
      chip(`as-${code}`, t("zoekresultaten.chip.animalStatus", { args: [t(facetLabelKey("animalStatus", code))] }), () =>
        applyFilters({ ...filters, animalStatuses: filters.animalStatuses.filter((x) => x !== code) }),
      );
    for (const code of filters.species)
      chip(`sp-${code}`, t("zoekresultaten.chip.species", { args: [t(facetLabelKey("species", code))] }), () =>
        applyFilters({ ...filters, species: filters.species.filter((x) => x !== code) }),
      );
    for (const code of filters.relationTypes)
      chip(`rt-${code}`, t("zoekresultaten.chip.relationType", { args: [t(facetLabelKey("relationType", code))] }), () =>
        applyFilters({ ...filters, relationTypes: filters.relationTypes.filter((x) => x !== code) }),
      );
    for (const code of filters.relationRoles)
      chip(`rr-${code}`, t("zoekresultaten.chip.relationRole", { args: [t(facetLabelKey("relationRole", code))] }), () =>
        applyFilters({ ...filters, relationRoles: filters.relationRoles.filter((x) => x !== code) }),
      );
    for (const code of filters.invoiceStatuses)
      chip(`is-${code}`, t("zoekresultaten.chip.invoiceStatus", { args: [t(facetLabelKey("invoiceStatus", code))] }), () =>
        applyFilters({ ...filters, invoiceStatuses: filters.invoiceStatuses.filter((x) => x !== code) }),
      );
    if (filters.minDaysPresent > 0)
      chip("minDays", t("zoekresultaten.chip.minDays", { args: [filters.minDaysPresent] }), () =>
        applyFilters({ ...filters, minDaysPresent: 0 }),
      );

    if (chips.length === 0) return null;
    return <div className="fawkes-search-results__chips">{chips}</div>;
  }

  // ---- left facet panel (UNFILTERED counts) ----
  function renderLeftFilters(): ReactNode {
    const facetCount = (group: keyof SearchFacetCountsDto, code: string): number =>
      (facets[group] as Record<string, number> | undefined)?.[code] ?? 0;

    return (
      <>
        <div className="fawkes-search-results__left-header">
          <span className="fawkes-search-results__left-title">{t("zoekresultaten.filters.title")}</span>
          <button type="button" className="fawkes-search-results__link" onClick={clearAllFilters}>
            {t("zoekresultaten.filters.clearAll")}
          </button>
        </div>

        {activeTab === "alles" ? (
          <div className="fawkes-search-results__facet">
            <span className="fawkes-search-results__facet-title">{t("zoekresultaten.section.typeResult")}</span>
            {ALL_TYPES.map((type) =>
              renderCheckbox(
                `type-${type}`,
                t(TYPE_LABEL_KEY[type]),
                rawByType(type).length,
                filters.resultTypes.length === 0 || filters.resultTypes.includes(type),
                () => {
                  const current = filters.resultTypes.length === 0 ? [...ALL_TYPES] : [...filters.resultTypes];
                  const isOn = current.includes(type);
                  const nextRaw = isOn ? current.filter((x) => x !== type) : [...current, type];
                  const next = ALL_TYPES.filter((x) => nextRaw.includes(x));
                  applyFilters({ ...filters, resultTypes: next.length === 4 ? [] : next });
                },
              ),
            )}
          </div>
        ) : null}

        {renderFacetSection("search.group.animals", "animalStatus", ANIMAL_STATUSES, filters.animalStatuses, facetCount, (next) =>
          applyFilters({ ...filters, animalStatuses: next }),
        )}
        {renderFacetSection("zoekresultaten.section.species", "species", SPECIES, filters.species, facetCount, (next) =>
          applyFilters({ ...filters, species: next }),
        )}
        {renderFacetSection("search.group.relations", "relationType", RELATION_TYPES, filters.relationTypes, facetCount, (next) =>
          applyFilters({ ...filters, relationTypes: next }),
        )}
        {renderFacetSection("zoekresultaten.section.relationRole", "relationRole", RELATION_ROLES, filters.relationRoles, facetCount, (next) =>
          applyFilters({ ...filters, relationRoles: next }),
        )}
        {renderFacetSection("search.group.invoices", "invoiceStatus", INVOICE_STATUSES, filters.invoiceStatuses, facetCount, (next) =>
          applyFilters({ ...filters, invoiceStatuses: next }),
        )}

      </>
    );
  }

  function renderFacetSection<T extends string>(
    titleKey: string,
    facetGroup: keyof SearchFacetCountsDto,
    codes: readonly T[],
    active: T[],
    facetCount: (group: keyof SearchFacetCountsDto, code: string) => number,
    onChange: (next: T[]) => void,
  ): ReactNode {
    return (
      <div className="fawkes-search-results__facet">
        <span className="fawkes-search-results__facet-title">{t(titleKey)}</span>
        {codes.map((code) =>
          renderCheckbox(
            `${String(facetGroup)}-${code}`,
            t(facetLabelKey(String(facetGroup), code)),
            facetCount(facetGroup, code),
            active.includes(code),
            () => onChange(toggleCode(active, code, codes)),
          ),
        )}
      </div>
    );
  }

  function renderCheckbox(key: string, label: string, count: number, checked: boolean, onToggle: () => void): ReactNode {
    return (
      <button
        key={key}
        type="button"
        role="checkbox"
        aria-checked={checked}
        className={`fawkes-search-results__cb${checked ? " fawkes-search-results__cb--checked" : ""}`}
        onClick={onToggle}
      >
        <span className="fawkes-search-results__cb-box">{checked ? <CheckIcon width={12} height={12} /> : null}</span>
        <span className="fawkes-search-results__cb-label">{label}</span>
        <span className="fawkes-search-results__cb-count">{count}</span>
      </button>
    );
  }

  // ---- middle results ----
  function renderMiddle(): ReactNode {
    if (q.length < MIN_CHARS)
      return emptyState(<SearchIcon width={40} height={40} />, t("zoekresultaten.empty.noQuery"));
    if (litError)
      return emptyState(
        <AlertTriangleIcon width={40} height={40} />,
        t("zoekresultaten.empty.error"),
        t("zoekresultaten.action.clearQuery"),
        () => navigatePlainQuery(""),
      );
    if (litFetching && !results) return <p className="fawkes-muted">{t("common.loading")}</p>;

    const shown = ALL_TYPES.filter(typeShown);
    const totalFilteredVisible = shown.reduce((sum, type) => sum + filteredCount[type], 0);
    if (totalFilteredVisible === 0 && hasAnyDomainFilter(filters))
      return emptyState(
        <FilterIcon width={40} height={40} />,
        t("zoekresultaten.empty.facetsEmpty"),
        t("zoekresultaten.action.clearFilters"),
        clearAllFilters,
      );

    return <>{shown.map((type) => renderSection(type))}</>;
  }

  function renderSection(type: ResultType): ReactNode {
    const all = sortedFilteredByType(type);
    const rawLen = rawByType(type).length;
    const hits = singleType === type ? all.slice((filters.page - 1) * filters.pageSize, filters.page * filters.pageSize) : all;
    const labelKey = TYPE_LABEL_KEY[type];

    return (
      <section key={type} className="fawkes-search-results__section">
        <div className="fawkes-search-results__section-header">
          {groupTypeIcon(type)}
          <span className="fawkes-search-results__section-title">{t(labelKey)}</span>
          <span className="fawkes-search-results__section-count">({all.length})</span>
        </div>
        {all.length === 0 ? (
          <p className="fawkes-search-results__section-empty">{sectionEmptyText(type, rawLen)}</p>
        ) : (
          hits.map((hit) => renderHitCard(type, hit))
        )}
        {/* "Bekijk alle <type>" at the per-type cap → the type tab (Alles only; a type tab paginates). */}
        {activeTab === "alles" && rawLen >= RESULTS_LIMIT && type !== "stay" ? (
          <button
            type="button"
            className="fawkes-search-results__show-more"
            onClick={() => applyFilters({ ...emptyFilters(), query: filters.query, resultTypes: [type] })}
          >
            <span>{t(`zoekresultaten.showMore.${TYPE_TO_TAB[type]}`)}</span>
            <ChevronDownIcon width={15} height={15} />
          </button>
        ) : null}
      </section>
    );
  }

  function sectionEmptyText(type: ResultType, rawLen: number): string {
    const seg = TYPE_TO_TAB[type]; // dieren|relaties|facturen|verblijven
    return rawLen === 0
      ? t(`zoekresultaten.section.${seg}.emptyNone`, { args: [q] })
      : t(`zoekresultaten.section.${seg}.emptyFiltered`);
  }

  function renderHitCard(type: ResultType, hit: SearchHitDto, details?: ExecuteDetailDto[]): ReactNode {
    const open = () => onOpenHit((hit.targetType as SearchHitType) ?? (hit.type as SearchHitType), hit.targetId ?? hit.id, q);
    const detailLines = (details ?? []).map((d, i) => (
      <span key={i} className="fawkes-search-results__detail">
        {d.label}: {d.value}
      </span>
    ));

    let avatar: ReactNode;
    let center: ReactNode;
    let badge: ReactNode = null;

    if (type === "animal") {
      const { name, chip } = splitAnimalTitle(hit.title);
      const statusCode = hit.animalStatus ?? "unknown";
      const photoUrl = animalPhotoUrls[String(hit.targetId ?? hit.id)];
      avatar = photoUrl ? (
        <img className="fawkes-search-results__photo-img" src={photoUrl} alt="" aria-hidden="true" />
      ) : (
        <span className="fawkes-search-results__photo" aria-hidden="true">
          <HeartIcon width={20} height={20} />
        </span>
      );
      center = (
        <span className="fawkes-search-results__hit-center">
          <span className="fawkes-search-results__hit-title">{name}</span>
          {hit.subtitle ? <span className="fawkes-search-results__hit-meta">{hit.subtitle}</span> : null}
          {chip ? <span className="fawkes-search-results__hit-meta">{t("zoekresultaten.card.chip", { args: [chip] })}</span> : null}
          {detailLines}
        </span>
      );
      badge = (
        <span className={`fawkes-search-results__status-badge fawkes-search-results__status-badge--${ANIMAL_STATUS_VARIANT[statusCode] ?? "neutral"}`}>
          {t(animalStatusBadgeKey(statusCode))}
        </span>
      );
    } else {
      const icon = type === "relation" ? null : type === "invoice" ? <InvoiceIcon width={18} height={18} /> : <HomeIcon width={18} height={18} />;
      avatar =
        type === "relation" ? (
          <span className="fawkes-search-results__avatar fawkes-search-results__avatar--relation" aria-hidden="true">
            {hit.initials || "?"}
          </span>
        ) : (
          <span className={`fawkes-search-results__avatar fawkes-search-results__avatar--${type}`} aria-hidden="true">
            {icon}
          </span>
        );
      center = (
        <span className="fawkes-search-results__hit-center">
          <span className="fawkes-search-results__hit-title">{hit.title}</span>
          {hit.subtitle ? <span className="fawkes-search-results__hit-meta">{hit.subtitle}</span> : null}
          {detailLines}
        </span>
      );
      // Relation/invoice cards carry the TYPE-label badge (Jmix getBadgeLabel → "Relatie"/"Factuur");
      // the classified role/status drives only the facets/chips. Stays carry no badge.
      if (type === "relation")
        badge = <span className="fawkes-search-results__role-badge">{t("search.badge.relation")}</span>;
      else if (type === "invoice")
        badge = <span className="fawkes-search-results__status-badge fawkes-search-results__status-badge--neutral">{t("search.badge.invoice")}</span>;
    }

    return (
      <div
        key={`${type}-${hit.id}`}
        className="fawkes-search-results__hit"
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
      >
        {avatar}
        {center}
        {badge ? <span className="fawkes-search-results__hit-right">{badge}</span> : null}
        <span className="fawkes-search-results__open">
          <span>{t("common.open")}</span>
          <ChevronRightIcon width={15} height={15} />
        </span>
      </div>
    );
  }

  // ---- right sidebar (Recente / Snelle acties / Tip) ----
  function renderSidebar(): ReactNode {
    return (
      <>
        {/* Bewaarde analyses staan bovenaan: het zijn de vragen die iemand al eens de moeite waard
            vond, en die wil je eerder zien dan de zoekopdrachten van vanochtend. */}
        <div className="fawkes-search-results__card">
          <BewaardeAnalyses
            huidigeVraag={q}
            // Alleen bewaren wat ook echt een analyse opleverde; een lege belofte bewaren is erger
            // dan geen knop.
            kanBewaren={(aiResult?.groepen?.length ?? 0) > 0}
            onOpenen={(id) => {
              voerAnalyseUit(id)
                .then((res) => setAnalyseResultaat(res as ExecuteResponseDto))
                .catch(() => setAnalyseResultaat(null));
            }}
            t={t}
          />
        </div>
        <div className="fawkes-search-results__card">
          <div className="fawkes-search-results__card-header">
            <ClockIcon width={16} height={16} />
            <span>{t("search.recent.title")}</span>
          </div>
          {recent.length === 0 ? (
            <p className="fawkes-search-results__card-empty">{t("search.empty")}</p>
          ) : (
            recent.map((term) => (
              <button
                key={term}
                type="button"
                className={`fawkes-search-results__recent${term.toLowerCase() === q.toLowerCase() ? " fawkes-search-results__recent--active" : ""}`}
                onClick={() => navigatePlainQuery(term)}
              >
                <SearchIcon width={14} height={14} />
                <span className="fawkes-search-results__recent-term">{term}</span>
                <ChevronRightIcon width={14} height={14} />
              </button>
            ))
          )}
          <button type="button" className="fawkes-search-results__recent-clear" onClick={rememberCleared}>
            <TrashIcon width={14} height={14} />
            <span>{t("zoekresultaten.recent.clearAll")}</span>
          </button>
        </div>

        <div className="fawkes-search-results__card">
          <div className="fawkes-search-results__card-header">
            <BoltIcon width={16} height={16} />
            <span>{t("zoekresultaten.quickActions.title")}</span>
          </div>
          {renderQuickAction("animal", <PawIcon width={16} height={16} />, "zoekresultaten.quickActions.newAnimal")}
          {renderQuickAction("relation", <UserIcon width={16} height={16} />, "zoekresultaten.quickActions.newRelation")}
          {renderQuickAction("invoice", <InvoiceIcon width={16} height={16} />, "zoekresultaten.quickActions.newInvoice")}
          {renderQuickAction("stay", <HomeIcon width={16} height={16} />, "zoekresultaten.quickActions.newStay")}
        </div>

        <div className="fawkes-search-results__card fawkes-search-results__card--tip">
          <div className="fawkes-search-results__card-header">
            <LightbulbIcon width={16} height={16} />
            <span>{t("zoekresultaten.tip.title")}</span>
          </div>
          <p className="fawkes-search-results__tip-text">{t("zoekresultaten.tip.text")}</p>
        </div>
      </>
    );
  }

  function renderQuickAction(action: ResultType, icon: ReactNode, labelKey: string): ReactNode {
    return (
      <button type="button" className="fawkes-search-results__action" onClick={() => onQuickAction(action)}>
        {icon}
        <span className="fawkes-search-results__action-label">{t(labelKey)}</span>
        <ChevronRightIcon width={14} height={14} />
      </button>
    );
  }

  // ---- client-side pagination within the per-type cap (Decision PAGING; single-type tab only) ----
  function renderPagination(): ReactNode {
    if (!singleType || q.length < MIN_CHARS || litError) return null;
    const total = filteredCount[singleType];
    const totalPages = Math.ceil(total / filters.pageSize);
    if (totalPages <= 1) return null;
    const page = Math.min(filters.page, totalPages);
    return (
      <div className="fawkes-search-results__pagination">
        <button
          type="button"
          className="fawkes-search-results__page-btn"
          disabled={page <= 1}
          aria-label={t("zoekresultaten.pagination.prev")}
          onClick={() => goToPage(page - 1)}
        >
          ‹
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            aria-current={n === page}
            className={`fawkes-search-results__page-btn${n === page ? " fawkes-search-results__page-btn--active" : ""}`}
            onClick={() => goToPage(n)}
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          className="fawkes-search-results__page-btn"
          disabled={page >= totalPages}
          aria-label={t("zoekresultaten.pagination.next")}
          onClick={() => goToPage(page + 1)}
        >
          ›
        </button>
        <span className="fawkes-search-results__per-page">{t("zoekresultaten.pagination.perPage", { args: [filters.pageSize] })}</span>
      </div>
    );
  }

  // ---- AI interpretation panel (per-mode, FR21) ----
  function renderAi(): ReactNode {
    if (aiFetching && !aiResult) {
      return (
        <>
          {renderAiHeader()}
          <p className="fawkes-muted">{t("common.loading")}</p>
        </>
      );
    }
    if (!aiResult) return null;

    const mode = aiResult.mode;
    const runnable = mode === "structured_filter" || mode === "partial";
    return (
      <>
        <div className="fawkes-search-results__ai-panel">
          {renderAiHeader()}
          <span className="fawkes-search-results__ai-mode">{t(`zoekresultaten.aiMode.${mode}`)}</span>
          {aiResult.explanation ? (
            <p className="fawkes-search-results__ai-explanation">{aiResult.explanation}</p>
          ) : null}

          {runnable && aiResult.suggestions.length > 0 ? (
            <>
              <span className="fawkes-search-results__ai-label-header">{t("zoekresultaten.ai.interpretedAs")}</span>
              <div className="fawkes-search-results__ai-labels">
                {aiResult.suggestions.map((s, i) => (
                  <span key={i} className="fawkes-search-results__ai-label">
                    {s}
                  </span>
                ))}
              </div>
            </>
          ) : null}

          {runnable && aiResult.usedDataAreas.length > 0 ? (
            <>
              <span className="fawkes-search-results__ai-label-header">{t("zoekresultaten.ai.usedData")}</span>
              <div className="fawkes-search-results__ai-labels">
                {aiResult.usedDataAreas.map((a, i) => (
                  <span key={i} className="fawkes-search-results__ai-area">
                    {a}
                  </span>
                ))}
              </div>
            </>
          ) : null}

          {runnable && aiResult.unsupportedParts.length > 0 ? (
            <p className="fawkes-search-results__ai-partial">
              {t("zoekresultaten.ai.unsupported", { args: [aiResult.unsupportedParts.join("; ")] })}
            </p>
          ) : null}

          {mode === "how_to" && getoondeHowTo ? (
            <div className="fawkes-search-results__howto">
              <p className="fawkes-search-results__howto-titel">{getoondeHowTo.titel}</p>
              <ol className="fawkes-search-results__howto-stappen">
                {getoondeHowTo.stappen.map((stap, i) => (
                  <li key={i}>{stap}</li>
                ))}
              </ol>
              <div className="fawkes-search-results__howto-knoppen">
                {heeftRondleiding(getoondeHowTo.id) ? (
                  <button
                    type="button"
                    className="fui-button fui-button--primary"
                    onClick={() => onStartRondleiding(getoondeHowTo.id)}
                  >
                    {t("zoekresultaten.howTo.laatZien")}
                  </button>
                ) : null}
                {getoondeHowTo.route ? (
                  <button
                    type="button"
                    className={heeftRondleiding(getoondeHowTo.id)
                      ? "fui-button fui-button--secondary" : "fui-button fui-button--primary"}
                    onClick={() => onOpenRoute(getoondeHowTo.route!)}
                  >
                    {t("zoekresultaten.howTo.ganaar")}
                  </button>
                ) : null}
              </div>
              {keuze.length > 1 ? (
                <button
                  type="button"
                  className="fawkes-search-results__howto-terug"
                  onClick={() => setGekozen(null)}
                >
                  {t("zoekresultaten.howTo.andereKeuze")}
                </button>
              ) : null}
            </div>
          ) : null}

          {/* Twee lezingen die even goed passen. Er dan één van tonen is een gok; de vrager weet
              zelf wel wat hij bedoelt en één klik kost hem niets. */}
          {mode === "how_to" && !getoondeHowTo && keuze.length > 1 ? (
            <div className="fawkes-search-results__howto fawkes-search-results__howto--keuze">
              <p className="fawkes-search-results__howto-titel">
                {t("zoekresultaten.howTo.watBedoelJe")}
              </p>
              <div className="fawkes-search-results__howto-opties">
                {keuze.map((optie) => (
                  <button
                    key={optie.id}
                    type="button"
                    className="fawkes-search-results__howto-optie"
                    onClick={() => setGekozen(optie.id)}
                  >
                    <span className="fawkes-search-results__howto-optie-titel">{optie.titel}</span>
                    {/* De eerste stap noemt het scherm ("Open Instellingen…", "Open het
                        Dashboard…"). Die hier alvast tonen scheelt een klik om te zien of je de
                        goede kant op gaat — kiezen zonder te weten waarheen is geen keuze. */}
                    {optie.stappen.length > 0 ? (
                      <span className="fawkes-search-results__howto-optie-weg">{optie.stappen[0]}</span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* Een telvraag levert getallen per groep in plaats van regels. Welke weergave past leest
              het onderdeel zelf af aan de gegevens; de gebruiker hoeft niet eerst een rapporttype te
              kiezen. */}
          {(aiResult.groepen?.length ?? 0) > 0 ? (
            <Samenvatting
              groepen={aiResult.groepen ?? []}
              groeperingen={aiResult.groeperingen ?? []}
              maat={aiResult.maat === "GEMIDDELDE" ? "GEMIDDELDE" : "AANTAL"}
              onUitklappen={(labels) => setUitgeklapt(labels)}
              // De vraag en de filters zoals ze boven het resultaat staan, zodat de export precies
              // dezelfde herkomst draagt als wat je op het scherm ziet.
              export={{
                vraag: q,
                filters: aiResult.suggestions ?? [],
                // De doorgeklikte groep gaat mee, zodat het bestand dezelfde regels bevat als het
                // scherm waar de knop op staat. Staat er geen staaf open, dan is dat undefined en
                // exporteert de server de samenvatting zelf.
                onExcel: (weergave) => exporteerExcel({ vraag: q, labels: uitgeklapt ?? undefined, grafiek: weergave }),
              }}
              t={t}
            />
          ) : null}

          {/* Een lijstantwoord is net zo goed een analyse als een telling, en hoort dus ook mee te
              kunnen. De knop staat alleen hier en niet boven de gewone zoekresultaten: dit is de
              lijst die uit een vraag met filters komt, en alleen daarvan weet de server welke
              afbakening er in de verantwoording hoort. */}
          {mode === "structured_filter" && (aiResult.groepen?.length ?? 0) === 0
            && (aiResult.total ?? 0) > 0 ? (
            <div className="fawkes-samenvatting__exportbalk">
              <button
                type="button"
                className="fui-button fui-button--secondary"
                disabled={lijstExportBezig}
                onClick={() => {
                  setLijstExportFout(null);
                  setLijstExportBezig(true);
                  // De server haalt de hele lijst op en niet alleen de zichtbare pagina. Een export
                  // die stilzwijgend bij record 50 ophoudt ziet er in Excel uit als een compleet
                  // antwoord, en dat is de fout die je nooit meer terugvindt.
                  void exporteerExcel({ vraag: q })
                    .catch((e: unknown) =>
                      setLijstExportFout(
                        e instanceof Error ? e.message : t("zoekresultaten.samenvatting.exportFout")))
                    .finally(() => setLijstExportBezig(false));
                }}
              >
                {lijstExportBezig
                  ? t("zoekresultaten.samenvatting.exportBezig")
                  : t("zoekresultaten.samenvatting.exportExcel")}
              </button>
              {lijstExportFout ? (
                <span className="fawkes-samenvatting__fout" role="alert">{lijstExportFout}</span>
              ) : null}
            </div>
          ) : null}

          {/* Staat er een staaf open, dan zijn de regels eronder wat je ziet — met de weg terug. */}
          {uitgeklapt ? (
            <div className="fawkes-samenvatting__terugbalk">
              <span>{t("zoekresultaten.samenvatting.onder")} {uitgeklapt.join(" · ")}</span>
              <button type="button" className="fawkes-search-results__howto-terug"
                onClick={() => setUitgeklapt(null)}>
                {t("zoekresultaten.samenvatting.terug")}
              </button>
            </div>
          ) : null}

          {mode === "clarification" ? renderClarification() : null}
          {mode === "out_of_scope" || mode === "rejected" ? (
            <p className="fawkes-search-results__ai-notice">{t("zoekresultaten.ai.notExecuted")}</p>
          ) : null}
        </div>

        {runnable ? renderAiResults() : null}
      </>
    );
  }

  function renderAiHeader(): ReactNode {
    return (
      <div className="fawkes-search-results__ai-header">
        <SparklesIcon className="fawkes-search-results__ai-icon" width={18} height={18} />
        <span className="fawkes-search-results__ai-title">{t("zoekresultaten.ai.title", { args: [q] })}</span>
        <button
          type="button"
          className="fawkes-search-results__ai-close"
          aria-label={t("common.close")}
          onClick={() => onSearchNavigate("", "", false)}
        >
          <XIcon width={16} height={16} />
        </button>
      </div>
    );
  }

  function renderClarification(): ReactNode {
    if (!aiResult) return null;
    return (
      <>
        {aiResult.question ? <p className="fawkes-search-results__ai-question">{aiResult.question}</p> : null}
        {aiResult.suggestions.length > 0 ? (
          <div className="fawkes-search-results__ai-suggestions">
            {aiResult.suggestions.map((s, i) => (
              <button key={i} type="button" className="fawkes-search-results__ai-suggestion" onClick={() => navigateAiQuery(s)}>
                {s}
              </button>
            ))}
          </div>
        ) : null}
      </>
    );
  }

  function renderAiResults(): ReactNode {
    if (!aiResult) return null;
    if (aiResult.items.length === 0)
      return <p className="fawkes-search-results__section-empty">{t("search.results.empty", { args: [q] })}</p>;
    return (
      <div className="fawkes-search-results__ai-items">
        {aiResult.items.map((item) => {
          const type = (["animal", "relation", "invoice", "stay"].includes(item.type) ? item.type : "animal") as ResultType;
          const hit: SearchHitDto = {
            type: type as SearchHitType,
            id: item.id,
            title: item.title,
            subtitle: item.subtitle,
            initials: item.initials,
            targetType: (item.targetType as SearchHitType) ?? null,
            targetId: item.targetId,
            animalStatus: null,
            species: null,
            admissionDate: null,
            relationType: null,
            relationRoles: null,
            invoiceStatus: null,
          };
          return renderHitCard(type, hit, item.details);
        })}
      </div>
    );
  }

  function emptyState(icon: ReactNode, text: string, actionLabel?: string, onAction?: () => void): ReactNode {
    return (
      <div className="fawkes-search-results__empty-state">
        <span className="fawkes-search-results__empty-icon">{icon}</span>
        <p className="fawkes-search-results__empty-text">{text}</p>
        {actionLabel && onAction ? (
          <button type="button" className="fawkes-search-results__link" onClick={onAction}>
            {actionLabel}
          </button>
        ) : null}
      </div>
    );
  }
}

/** Admission-date comparator: hits with a date sort by it (dir −1 = newest first); missing dates last. */
/**
 * De vroegste opnamedatum die nog meetelt bij een gekozen periode, of {@code null} bij "Alles".
 *
 * <p>Gerekend vanaf vandaag: een week is zeven dagen terug, een maand dertig, een jaar 365. Geen
 * kalenderweek dus — wie op maandagochtend "deze week" kiest wil zien wat er de afgelopen dagen
 * binnenkwam, niet de twee dieren van vanochtend.
 */
export function periodeGrens(period: Period, nu = new Date()): string | null {
  const dagen = period === "week" ? 7 : period === "month" ? 30 : period === "year" ? 365 : null;
  if (dagen == null) return null;
  const grens = new Date(nu);
  grens.setDate(grens.getDate() - dagen);
  return grens.toISOString().slice(0, 10);
}

/**
 * Valt deze treffer binnen de gekozen periode?
 *
 * <p>Alleen dieren dragen een opnamedatum. Een relatie of een factuur heeft er geen, en die
 * verdwijnen dus niet zodra iemand een periode kiest — anders zou "deze week" de halve
 * zoekopdracht leegvegen zonder dat er iets mis is.
 */
export function binnenPeriode(hit: SearchHitDto, period: Period, nu = new Date()): boolean {
  const grens = periodeGrens(period, nu);
  if (grens == null || !hit.admissionDate) return true;
  return hit.admissionDate.slice(0, 10) >= grens;
}

/** Het dubbele pijltje van de sorteerknop — Vaadin's VaadinIcon.SORT, nagetekend. */
function SortArrowsIcon(): ReactNode {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M8 1.5 4.5 6h7L8 1.5Z" fill="currentColor" />
      <path d="M8 14.5 4.5 10h7L8 14.5Z" fill="currentColor" />
    </svg>
  );
}

function compareAdmission(a: SearchHitDto, b: SearchHitDto, dir: 1 | -1): number {
  const da = a.admissionDate;
  const db = b.admissionDate;
  if (da && db) return da < db ? dir : da > db ? -dir : 0;
  if (da) return -1;
  if (db) return 1;
  return 0;
}
